import { css } from '@emotion/react';

export default $ => css`
  .collapsable-tree-select {
    width: 100%;
    position: relative;

    .tree-select-toggle {
      display: flex;
      align-items: center;
      cursor: pointer;
      user-select: none;
      height: 40px;
      padding-right: 32px;
      position: relative;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='${encodeURIComponent(
        $.primaryMain ?? '#0d6efd'
      )}' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 12px center;
      transition: background-image 0.2s ease, border-color 0.15s;

      &.open {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='${encodeURIComponent(
          $.primaryMain ?? '#0d6efd'
        )}' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 4.86 2.451 10.342C1.885 10.987 2.345 12 3.204 12h9.592a1 1 0 0 0 .753-1.659L8.753 4.86a1 1 0 0 0-1.506 0z'/%3E%3C/svg%3E");
      }

      &.disabled {
        opacity: 0.65;
        cursor: default;
        background-color: ${$.gray200 ?? '#e9ecef'};
      }

      .tree-select-value {
        flex: 1;
        min-width: 0;
        overflow: hidden;

        .label {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          &.as-placeholder {
            color: ${$.gray400};
          }
        }
      }
    }
  }

  .tree-select-path-hint {
    font-size: 0.72em;
    color: ${$.gray500 ?? '#6c757d'};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 1px 2px 0;
    margin-top: 4px;
  }

  .input-wrapper .collapsable-tree-select,
  .input-group .collapsable-tree-select {
    flex: 1;
    min-width: 0;
  }

  /* ── Portal menu — rendered in document.body, positioned via inline style ── */

  .tree-select-menu {
    background: ${$.white ?? '#fff'};
    border: 1px solid ${$.cardBorderColor ?? '#dee2e6'};
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .tree-select-search {
      padding: 8px;
      border-bottom: 1px solid ${$.cardBorderColor ?? '#dee2e6'};
      background: ${$.white ?? '#fff'};
      flex-shrink: 0;
    }

    .tree-select-items {
      max-height: 320px;
      overflow-y: auto;
    }

    .tree-select-empty {
      padding: 10px 14px;
      color: ${$.gray500 ?? '#6c757d'};
      font-style: italic;
      font-size: 0.875em;
    }

    /* ── Tree nodes ── */

    .tree-node {
      display: flex;
      align-items: center;
      padding: 5px 10px 5px 0;
      gap: 4px;
      border-bottom: 1px solid ${$.gray100 ?? '#f3f4f6'};
      transition: background 0.1s;

      &:last-child {
        border-bottom: none;
      }

      &.tree-node-selectable {
        cursor: pointer;

        &:hover {
          background: ${$.primaryMain ? `${$.primaryMain}10` : '#eef3ff'};

          .tree-node-label {
            color: ${$.primaryMain ?? '#0d6efd'};
          }
        }

        &:active {
          background: ${$.primaryMain ? `${$.primaryMain}1e` : '#dde8ff'};
        }
      }

      &.tree-node-disabled {
        cursor: default;

        .tree-node-label {
          color: ${$.gray400 ?? '#9ca3af'};
        }
      }

      &.tree-node-selected {
        background: ${$.primaryMain ? `${$.primaryMain}18` : '#f0f7ff'};

        .tree-node-label {
          font-weight: 600;
          color: ${$.primaryMain ?? '#0d6efd'};
        }

        &.tree-node-selectable:hover {
          background: ${$.primaryMain ? `${$.primaryMain}28` : '#dde8ff'};
        }
      }

      &.tree-node-flat {
        padding-left: 10px;
        gap: 8px;

        .tree-node-flat-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
      }

      .tree-node-toggle-btn {
        flex-shrink: 0;
        width: 20px !important;
        height: 20px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 0 !important;
        background: none !important;
        border: 1px solid transparent !important;
        border-radius: 3px !important;
        color: ${$.gray500 ?? '#6c757d'} !important;
        line-height: 1 !important;
        text-decoration: none !important;
        box-shadow: none !important;
        transition: color 0.15s, background 0.15s, border-color 0.15s !important;

        i {
          font-size: 0.65em;
          pointer-events: none;
        }

        &:hover,
        &:focus {
          background: ${$.gray200 ?? '#e9ecef'} !important;
          border-color: ${$.cardBorderColor ?? '#dee2e6'} !important;
          color: ${$.gray800 ?? '#333'} !important;
          text-decoration: none !important;
          box-shadow: none !important;
        }

        &:active {
          background: ${$.gray300 ?? '#dee2e6'} !important;
          border-color: ${$.cardBorderColor ?? '#dee2e6'} !important;
          color: ${$.gray800 ?? '#333'} !important;
          box-shadow: none !important;
        }
      }

      .tree-node-label-group {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 5px;
        overflow: hidden;
      }

      .tree-node-label {
        font-size: 0.875em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
        flex-shrink: 1;
      }

      .tree-node-breadcrumb {
        font-size: 0.72em;
        color: ${$.gray400 ?? '#9ca3af'};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .tree-node-info-badge {
        flex-shrink: 0;
        font-size: 0.6em !important;
        padding: 1px 5px !important;
        line-height: 1.3 !important;
      }

      .tree-node-select-btn {
        flex-shrink: 0;
        padding: 1px 9px !important;
        font-size: 0.72em !important;
        line-height: 1.5 !important;
        white-space: nowrap;
      }

      .tree-node-unselectable-badge {
        flex-shrink: 0;
        width: 40px;
        text-align: center;
        font-size: 0.75em;
        color: ${$.gray300 ?? '#d1d5db'};
        user-select: none;
      }
    }

    /* ── Ancestor indicator for hidden selected node ── */

    .tree-node-selected-indicator {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px 4px 0;
      background: ${$.primaryMain ? `${$.primaryMain}12` : '#e8f0fe'};
      border-left: 2px solid ${$.primaryMain ?? '#0d6efd'};
      font-size: 0.8em;
      color: ${$.primaryMain ?? '#0d6efd'};

      i {
        flex-shrink: 0;
        font-size: 0.9em;
      }

      .tree-node-selected-indicator-label {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-style: italic;
      }

      .tree-node-expand-btn {
        flex-shrink: 0;
        padding: 0 4px !important;
        font-size: 0.85em !important;
        color: ${$.primaryMain ?? '#0d6efd'} !important;
        text-decoration: underline;
        line-height: 1.4 !important;

        &:hover {
          opacity: 0.75;
        }
      }
    }
  }
`;
